<template>
    <page-loading v-if="loading"></page-loading>
    <vx-page-layout v-else layout="sidebar">
        <template v-slot:title>
            <span data-test="message-standard-header">Default Standards</span>
        </template>

        <template v-slot:subtitle>
            <a
                :href="`/settings/teams/${teamId}#/message-standards`"
                class="no-underline! text-sky-600! text-base font-normal"
            >
                <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                All Brand Standards
            </a>
        </template>
        <template v-slot:sidebar>
            <page-sidebar appearance="nested" :links="sidebarLinks" data-test="message-standard-sidebar"></page-sidebar>
        </template>

        <form-wrapper
            v-if="!targetsView"
            :sections="[
                { id: 'marketing-rules', label: 'Marketing Campaign' },
                { id: 'transactional-rules', label: 'Transactional Campaign' },
                { id: 'message-hub-rules', label: 'Message Hub' },
            ]"
            :hideNav="true"
        >
            <message-standard-rules-form
                ref="rulesForm"
                v-model="messageStandard"
                :showRulesInitially="true"
                v-model:marketingEnabled="marketingRulesEnabled"
                v-model:transactionalEnabled="transactionalRulesEnabled"
                v-model:messageHubEnabled="messageHubRulesEnabled"
            />

            <vx-sticky-footer>
                <div class="flex flex-col-reverse gap-2 w-full grow lg:flex-row">
                    <vx-button
                        :href="`/settings/teams/${teamId}#/message-standards`"
                        block
                        class="grow lg:w-20"
                        size="lg"
                        color="muted"
                    >
                        Cancel
                    </vx-button>
                    <vx-button
                        @click="submit()"
                        data-test="submit-button"
                        block
                        class="grow"
                        size="lg"
                        color="primary"
                        :loading="processing"
                    >
                        Update Default Standard
                    </vx-button>
                </div>
            </vx-sticky-footer>
        </form-wrapper>

        <div v-if="targetsView" class="w-full md:max-w-2xl">
            <vx-alert color="slate" shadow :closeable="false">
                <strong>Default Standards</strong> apply when no manual Brand Standard is set.
            </vx-alert>
        </div>
    </vx-page-layout>
</template>

<script setup lang="ts">
import { VxAlert, VxButton, VxPageLayout, VxStickyFooter } from '@voxie/frontend-components';
import { ref, onMounted, nextTick, computed } from 'vue';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { messageStandardsClient } from '~/services';
import FormWrapper from '~/components/general/FormWrapper.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import { defaultMessageStandard, readMessageStandard } from './useMessageStandardDetails';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import MessageStandardRulesForm from './MessageStandardRulesForm.vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const teamId = Spark.state.currentTeam.id;
const toasted = useToasted();

const targetsView = ref(false);

const sidebarLinks = computed(() => [
    { active: !targetsView.value, onClick: () => (targetsView.value = false), href: '#', label: 'Details' },
    {
        onClick: () => (targetsView.value = true),
        href: '#',
        label: 'Targets',
        active: targetsView.value,
    },
]);

const messageStandard = ref(defaultMessageStandard());
const loading = ref(true);
const rulesForm = ref<InstanceType<typeof MessageStandardRulesForm>>();
const marketingRulesEnabled = ref(true);
const transactionalRulesEnabled = ref(true);
const messageHubRulesEnabled = ref(true);

onMounted(async () => {
    try {
        loading.value = true;
        const response = await messageStandardsClient.getDefaultMessageStandard({ teamId });
        messageStandard.value = readMessageStandard(response.data);

        loading.value = false;

        await nextTick();
        if (rulesForm.value) {
            marketingRulesEnabled.value = Boolean(rulesForm.value.marketingCount);
            transactionalRulesEnabled.value = Boolean(rulesForm.value.transactionalCount);
            messageHubRulesEnabled.value = Boolean(rulesForm.value.messageHubCount);
        }
    } catch (e) {
        toasted.error(catchRequestError(e));
    }
});

const save = async () => {
    // janky but necessary in order to lose reference of nested
    // objects such as `ai_message_vetting_agents` that is an array
    const data = JSON.parse(JSON.stringify(messageStandard.value));

    if (!marketingRulesEnabled.value) {
        data.marketing_rules = undefined;
    } else {
        delete data.marketing_rules?.ai_message_vetting_agents;
    }

    if (messageStandard.value.transactional_config !== null) {
        data.transactional_rules = undefined;
    }

    if (!transactionalRulesEnabled.value) {
        data.transactional_config = undefined;
        data.transactional_rules = undefined;
    } else {
        delete data.transactional_rules?.ai_message_vetting_agents;
    }

    if (!messageHubRulesEnabled.value) {
        data.message_hub_rules = undefined;
    }

    const response = await messageStandardsClient.updateDefaultMessageStandard({ teamId }, data);

    messageStandard.value = readMessageStandard(response.data);
};

const { submit, processing } = useFormSubmission({
    data: messageStandard,
    validation: {},
    preserveScroll: () => true,
    submit: save,
    reset: () => {},
    notification: () => ({
        message: 'Default Brand Standard updated successfully',
    }),
});
</script>
